
import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'neon' | 'glass' | 'cyber' | 'soundcharts' | 'news' | 'gradient' | 'ocean' | 'sunset' | 'forest' | 'futuristic' | 'neo' | 'game' | 'arcade';
  }
>(({ className, variant = 'default', ...props }, ref) => {
  const variantClasses = {
    default: "rounded-lg border bg-card text-card-foreground shadow-sm",
    neon: "rounded-lg border border-neon-blue/30 bg-black/80 text-white shadow-neon backdrop-blur-lg",
    glass: "rounded-lg border border-white/20 bg-white/10 text-white backdrop-blur-lg",
    cyber: "rounded-lg border-0 bg-gradient-to-tr from-neon-blue/20 via-neon-purple/20 to-neon-pink/20 text-white backdrop-blur-lg relative overflow-hidden",
    soundcharts: "rounded-lg border border-soundcharts-purple/30 bg-white/95 dark:bg-soundcharts-darkestPurple/95 text-foreground shadow-soundcharts backdrop-blur-md",
    news: "rounded-lg border border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-800 text-foreground shadow-sm hover:shadow-md transition-shadow duration-200",
    gradient: "rounded-lg border-0 bg-gradient-to-br from-blue-500/90 to-purple-600/90 text-white/95 shadow-lg",
    ocean: "rounded-lg border-0 bg-gradient-to-r from-cyan-500/90 to-blue-500/90 text-white/95 shadow-lg",
    sunset: "rounded-lg border-0 bg-gradient-to-r from-orange-400/90 to-pink-500/90 text-white/95 shadow-lg",
    forest: "rounded-lg border-0 bg-gradient-to-r from-emerald-500/90 to-green-600/90 text-white/95 shadow-lg",
    futuristic: "rounded-xl border border-white/15 bg-black/60 text-white/95 backdrop-blur-md shadow-lg relative overflow-hidden",
    neo: "rounded-xl border border-white/15 bg-black/50 text-white/95 backdrop-blur-sm shadow-lg futuristic-panel",
    game: "rounded-xl border border-white/15 bg-gradient-to-br from-black/70 via-black/60 to-black/70 text-white/95 backdrop-blur-sm shadow-lg futuristic-panel",
    arcade: "rounded-xl border-2 border-neon-pink/50 bg-black/80 text-white/95 backdrop-blur-sm shadow-neon-pink relative overflow-hidden"
  };

  // Add futuristic highlighting border effect if variant is futuristic or neo
  const isFuturistic = variant === 'futuristic' || variant === 'neo' || variant === 'game' || variant === 'arcade';

  return (
    <div
      ref={ref}
      className={cn(
        variantClasses[variant],
        isFuturistic && "before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-neon-purple/50 before:to-transparent",
        variant === 'arcade' && "after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:bg-gradient-to-r after:from-transparent after:via-neon-blue/50 after:to-transparent",
        className
      )}
      {...props}
    />
  )
})
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
